import React, { useCallback, useRef, useState, useEffect } from "react";
import axios from 'axios';
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faDownload, faFilePdf, faUpload, faFileAlt, faFillDrip, faUndo, faRedo, faSlash, faCircle, faSquare
} from "@fortawesome/free-solid-svg-icons";
import { jsPDF } from "jspdf";

const HandwrittenNote = ({ width = 794, height = 1024, onClose, pageNumber, bookTitle }) => {
  const authState = useSelector((state) => state.authReducer);
  const navigate = useNavigate();
  const [userId] = useState(authState.user._id);
  const [userCode] = useState(authState.user.think5whyscode);

  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [convertedText, setConvertedText] = useState('');
  const [uploadImageClicked, setUploadImageClicked] = useState(false);
  const [downloadImageClicked, setDownloadImageClicked] = useState(false);
  const [convertToTextClicked, setConvertToTextClicked] = useState(false);

  const drawColor = 'black';
  const [penSize, setPenSize] = useState(1); 
  const [penColor, setPenColor] = useState('black'); 
  const [shapeMode, setShapeMode] = useState('free'); // mode can be 'free', 'line', 'circle', 'rectangle'
  const [actionsHistory, setActionsHistory] = useState([]); // For undo/redo
  const [currentActionIndex, setCurrentActionIndex] = useState(-1);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const parent = canvas.parentElement;
      if (parent) {
        const parentWidth = parent.clientWidth;
        const parentHeight = parent.clientHeight;
  
        // Set the real drawing size (not just style)
        canvas.width = parentWidth;
        canvas.height = parentHeight;
      }
    }
  }, []);

  const handleFillCanvas = () => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.fillStyle = penColor;
    ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
  };

  const handleUndo = () => {
    if (currentActionIndex <= 0) return;
    setCurrentActionIndex(currentActionIndex - 1);
    const ctx = canvasRef.current.getContext('2d');
    const action = actionsHistory[currentActionIndex - 1];
    ctx.putImageData(action, 0, 0); // Restore the previous canvas state
  };

  const handleRedo = () => {
    if (currentActionIndex >= actionsHistory.length - 1) return;
    setCurrentActionIndex(currentActionIndex + 1);
    const ctx = canvasRef.current.getContext('2d');
    const action = actionsHistory[currentActionIndex + 1];
    ctx.putImageData(action, 0, 0); // Restore the next canvas state
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = drawColor;
    ctx.lineWidth = penSize;

    const handleResize = () => {
      const newParentWidth = canvas.parentElement.offsetWidth;
      const currentImage = canvas.toDataURL('image/png');

      canvas.width = newParentWidth;
      canvas.height = height;

      const img = new Image();
      img.src = currentImage;
      img.onload = () => {
        ctx.drawImage(img, 0, 0, newParentWidth, height);
      };

      ctx.strokeStyle = drawColor;
      ctx.lineWidth = penSize;
    };

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [height, drawColor]);

  const saveCanvasState = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const currentState = ctx.getImageData(0, 0, canvas.width, canvas.height);
    setActionsHistory(prev => {
      const updatedHistory = prev.slice(0, currentActionIndex + 1);
      updatedHistory.push(currentState);
      return updatedHistory;
    });
    setCurrentActionIndex(prev => prev + 1);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const getCanvasContext = () => {
      const ctx = canvas.getContext('2d');
      ctx.strokeStyle = penColor;
      ctx.lineWidth = penSize;
      return ctx;
    };

    const startDrawing = (x, y) => {
      const ctx = getCanvasContext();
      ctx.beginPath();
      ctx.moveTo(x, y);
      setIsDrawing(true);
    };

    const draw = (x, y) => {
      if (!isDrawing) return;
      const ctx = getCanvasContext();
      if (shapeMode === 'free') {
        ctx.lineTo(x, y);
        ctx.stroke();
      } else if (shapeMode === 'line') {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x + 100, y + 100); // Example, modify as needed
        ctx.stroke();
      } else if (shapeMode === 'circle') {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.beginPath();
        ctx.arc(x, y, 50, 0, 2 * Math.PI);
        ctx.stroke();
      } else if (shapeMode === 'rectangle') {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.beginPath();
        ctx.rect(x, y, 100, 50); // Example, modify as needed
        ctx.stroke();
      }
    };

    const endDrawing = () => {
      if (isDrawing) {
        const ctx = getCanvasContext();
        ctx.closePath();
        setIsDrawing(false);
        saveCanvasState(); // Save the drawing state after drawing ends
      }
    };

    const getMousePos = (e) => {
      const rect = canvas.getBoundingClientRect();
      return {
        x: (e.clientX - rect.left) * (canvas.width / rect.width),
        y: (e.clientY - rect.top) * (canvas.height / rect.height),
      };
    };

    const getTouchPos = (e) => {
      const rect = canvas.getBoundingClientRect();
      return {
        x: (e.touches[0].clientX - rect.left) * (canvas.width / rect.width),
        y: (e.touches[0].clientY - rect.top) * (canvas.height / rect.height),
      };
    };

    const handleMouseDown = (e) => {
      const { x, y } = getMousePos(e);
      startDrawing(x, y);
    };

    const handleMouseMove = (e) => {
      if (!isDrawing) return;
      const { x, y } = getMousePos(e);
      draw(x, y);
    };

    const handleTouchStart = (e) => {
      e.preventDefault();
      const { x, y } = getTouchPos(e);
      startDrawing(x, y);
    };

    const handleTouchMove = (e) => {
      e.preventDefault();
      if (!isDrawing) return;
      const { x, y } = getTouchPos(e);
      draw(x, y);
    };

    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseup', endDrawing);
    canvas.addEventListener('mouseout', endDrawing);

    canvas.addEventListener('touchstart', handleTouchStart);
    canvas.addEventListener('touchmove', handleTouchMove);
    canvas.addEventListener('touchend', endDrawing);

    return () => {
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseup', endDrawing);
      canvas.removeEventListener('mouseout', endDrawing);

      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', endDrawing);
    };
  }, [isDrawing, penColor, penSize, shapeMode, actionsHistory, currentActionIndex]);


  const getCurrentTimestamp = () => {
    const now = new Date();
    return `${now.toISOString().slice(0, 10).replace(/-/g, '')}${now.toTimeString().slice(0, 8).replace(/:/g, '')}`;
  };

  const convertBase64ToBlob = (base64String) => {
    const byteString = atob(base64String.split(',')[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
    return new Blob([ab], { type: 'image/png' });
  };

  const handleUploadImage = async () => {
    try {
      setUploadImageClicked(true);
      setError('');
      setLoading(true);

      const canvas = canvasRef.current;
      if (!canvas) throw new Error("Canvas not found.");

      const imageBlob = convertBase64ToBlob(canvas.toDataURL("image/png"));
      const formData = new FormData();
      const imgFileName = `notes_${pageNumber}_of_${bookTitle}_${userCode}_${getCurrentTimestamp()}.png`;
      formData.append("file", imageBlob, imgFileName);

      const response = await axios.post("/api/uploads/upload-usernotes", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.status === 200) {
        alert("Image uploaded successfully!");
      } else {
        throw new Error(`Failed to upload image. Server responded with status: ${response.status}`);
      }
    } catch (error) {
      console.error("Upload error:", error);
      setError(error.message || "An error occurred during upload.");
    } finally {
      setLoading(false);
    }
  };

  // const handleDownloadImage = async () => {
  //   setDownloadImageClicked(true);
  //   const canvas = canvasRef.current;
  //   if (!canvas) {
  //     setError('Canvas not found.');
  //     return;
  //   }

  //   const imageBlob = convertBase64ToBlob(canvas.toDataURL('image/png'));
  //   const blobURL = URL.createObjectURL(imageBlob);
  //   const link = document.createElement('a');
  //   link.href = blobURL;
  //   link.download = `notes_${userCode}_${getCurrentTimestamp()}.png`;
  //   document.body.appendChild(link);
  //   link.click();
  //   document.body.removeChild(link);
  //   URL.revokeObjectURL(blobURL);
  // };

  const handleDownloadImage = async () => {
    setDownloadImageClicked(true);
    const canvas = canvasRef.current;
    if (!canvas) {
      setError('Canvas not found.');
      return;
    }
  
    // Save current canvas size
    const originalWidth = canvas.width;
    const originalHeight = canvas.height;
  
    // Ensure no CSS scaling issues
    canvas.style.width = `${originalWidth}px`;
    canvas.style.height = `${originalHeight}px`;
  
    try {
      // Now create the image
      const imageBlob = convertBase64ToBlob(canvas.toDataURL('image/png'));
      const blobURL = URL.createObjectURL(imageBlob);
  
      const link = document.createElement('a');
      link.href = blobURL;
      link.download = `notes_${pageNumber}_of_${bookTitle}_${userCode}_${getCurrentTimestamp()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
  
      URL.revokeObjectURL(blobURL);
    } catch (error) {
      console.error('Error downloading image:', error);
      setError('Failed to download image.');
    } finally {
      // Restore canvas size if anything weird happened
      canvas.width = originalWidth;
      canvas.height = originalHeight;
      canvas.style.width = `${originalWidth}px`;
      canvas.style.height = `${originalHeight}px`;
    }
  };

  const handleDownloadPDF = async () => {
    const canvas = canvasRef.current;
    if (!canvas) {
      setError('Canvas not found.');
      return;
    }

    const pdf = new jsPDF('p', 'mm', 'a4');
    const imgWidth = 210;
    const imgHeight = (canvas.height / canvas.width) * imgWidth;
    pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, imgWidth, imgHeight);
    pdf.save(`notes_${pageNumber}_of_${bookTitle}_${userCode}_${getCurrentTimestamp()}.pdf`);
  };

  const handleConvertToText = async () => {
    setConvertToTextClicked(true);
    setLoading(true);
    setError('');
    setConvertedText('');

    const canvas = canvasRef.current;
    if (!canvas) {
      setError('Canvas not found.');
      setLoading(false);
      return;
    }

    const imageBlob = convertBase64ToBlob(canvas.toDataURL('image/png'));
    const formData = new FormData();
    formData.append('file', imageBlob, 'image.png');
    formData.append('language', 'eng');
    formData.append('apikey', 'K81755459088957'); 

    try {
      const response = await fetch('https://api.ocr.space/parse/image', {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();
      if (result.IsErroredOnProcessing) {
        throw new Error(result.ErrorMessage[0]);
      }
      setConvertedText(result.ParsedResults[0].ParsedText);
    } catch (error) {
      setError(error.message || "OCR error");
    } finally {
      setLoading(false);
    }
  };

  const handlePenSizeChange = (newPenSize) => {
    setPenSize(newPenSize);
  };

  return (
    <div className="flex flex-col items-center w-full min-h-screen bg-gray-100 p-4">
      <div className="flex flex-wrap justify-evenly">
        <p className="text-blue-700 text-sm text-center">
          Handwritten Notes for Page# {pageNumber} of {bookTitle}: Use Mouse on Laptop/Desktop, Stylus/Finger on Touchscreen Devices
        </p>
      </div>
      <div className="flex flex-wrap gap-4 mb-6 w-full bg-gray-300 p-4 border border-gray-400 rounded-lg shadow-lg justify-center">
        <div className="flex items-center gap-2">
          <button onClick={() => handlePenSizeChange(1)} className="bg-gray-200 rounded hover:bg-gray-300 p-2 flex items-center justify-center">
            <div className="w-4 bg-black" style={{ height: '1px' }}></div> {/* Very Thin Line */}
          </button>
          <button onClick={() => handlePenSizeChange(2)} className="bg-gray-200 rounded hover:bg-gray-300 p-2 flex items-center justify-center">
            <div className="w-4 bg-black" style={{ height: '2px' }}></div> {/* Thin Line */}
          </button>
          <button onClick={() => handlePenSizeChange(3)} className="bg-gray-200 rounded hover:bg-gray-300 p-2 flex items-center justify-center">
            <div className="w-4 bg-black" style={{ height: '3px' }}></div> {/* Medium Line */}
          </button>
          <button onClick={() => handlePenSizeChange(4)} className="bg-gray-200 rounded hover:bg-gray-300 p-2 flex items-center justify-center">
            <div className="w-4 bg-black" style={{ height: '4px' }}></div> {/* Thick Line */}
          </button>
        </div>
        <div className="flex items-center flex-wrap gap-2">
          {['violet', 'indigo', 'blue', 'green', 'yellow', 'orange', 'red', 'black', 'white', 'purple'].map(color => (
            <button
              key={color}
              onClick={() => setPenColor(color)}
              className="w-6 h-6 rounded-full border-2 border-gray-300 hover:border-black"
              style={{ backgroundColor: color }}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button className="bg-white" onClick={handleFillCanvas} title="Paint Canvas">
            <FontAwesomeIcon icon={faFillDrip} className="text-lg text-blue-600 hover:text-blue-800" />
          </button>
          <button className="bg-white" onClick={handleUndo} title="Undo">
            <FontAwesomeIcon icon={faUndo} className="text-lg text-yellow-600 hover:text-yellow-800" />
          </button>
          <button className="bg-white" onClick={handleRedo} title="Redo">
            <FontAwesomeIcon icon={faRedo} className="text-lg text-yellow-600 hover:text-yellow-800" />
          </button>
          {/* <button className="bg-white" onClick={() => setShapeMode('line')} title="Draw Line">
            <FontAwesomeIcon icon={faSlash} className="text-lg text-green-600 hover:text-green-800" />
          </button>
          <button className="bg-white" onClick={() => setShapeMode('circle')} title="Draw Circle">
            <FontAwesomeIcon icon={faCircle} className="text-lg text-purple-600 hover:text-purple-800" />
          </button>
          <button className="bg-white" onClick={() => setShapeMode('rectangle')} title="Draw Rectangle">
            <FontAwesomeIcon icon={faSquare} className="text-lg text-red-600 hover:text-red-800" />
          </button> */}
        </div>
        
      </div>
      <div className="relative w-full bg-white border border-gray-400 rounded-lg shadow-lg overflow-hidden" 
          style={{ height: '80vh' }}>
        <canvas
          ref={canvasRef}
          className="w-full h-full"
          style={{ touchAction: 'none' }}
        />
      </div>

      {/* Icon Buttons */}
      <div className="mt-6 flex flex-wrap gap-8 justify-center bg-gray-300 p-6 border border-gray-400 rounded-lg shadow-lg w-full">
        <div className="flex flex-col items-center">
          <FontAwesomeIcon
            icon={faDownload}
            onClick={handleDownloadImage}
            className="text-2xl cursor-pointer hover:text-blue-500"
            title="Download as Image"
          />
        </div>
        <div className="flex flex-col items-center">
          <FontAwesomeIcon
            icon={faFilePdf}
            onClick={handleDownloadPDF}
            className="text-2xl cursor-pointer hover:text-red-500"
            title="Download as PDF"
          />
        </div>
        <div className="flex flex-col items-center">
          <FontAwesomeIcon
            icon={faUpload}
            onClick={handleUploadImage}
            className="text-2xl cursor-pointer hover:text-green-500"
            title="Save This Handwritten Note"
          />
        </div>
        <div className="flex flex-col items-center">
          <FontAwesomeIcon
            icon={faFileAlt}
            className="text-2xl text-gray-400 cursor-not-allowed" // 👈 disabled style
            title="Convert to Text (Disabled)"
          />
        </div>
      </div>

      {/* Status Messages */}
      {loading && (
        <div className="text-center mt-4 text-blue-600 font-semibold">
          Processing...
        </div>
      )}
      {error && (
        <div className="text-center mt-4 text-red-500 font-semibold">
          {error}
        </div>
      )}
      {convertedText && (
        <div className="mt-6 w-full max-w-[794px] bg-white p-6 rounded-lg shadow-lg">
          <h3 className="text-lg font-bold mb-2">Converted Text:</h3>
          <p className="whitespace-pre-wrap">{convertedText}</p>
        </div>
      )}
    </div>
  );
};

export default HandwrittenNote;
